import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money, periodLabel } from "@/lib/format";
import { AlertCircle, ChevronRight, RefreshCw, Loader2 } from "lucide-react";

/**
 * "Prior-month payouts still unpaid" indicator with two rendering modes.
 *
 *   variant="compact" — used on Admin Overview. Renders a stacked list
 *   with one row per unpaid month showing the total for that month
 *   plus the partner count. Groups by ``period`` so a two-month arrears
 *   is unmistakable at a glance (July owed + June owed on their own
 *   lines, not collapsed into a single "2 payouts unpaid" summary).
 *
 *   variant="minimal" — used on Admin Ledger. A single inline chip so
 *   the current-month ledger view stays uncluttered.
 *
 * Both modes render nothing when there's nothing outstanding.
 */
export default function OutstandingPayoutsBanner({
  variant = "compact",
  testid = "outstanding-payouts-banner",
}) {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  async function reload() {
    try {
      const { data } = await api.get("/commissions/payouts/outstanding");
      setData(data);
    } catch (_) { /* silently degrade */ }
  }
  useEffect(() => { reload(); }, []);

  // "Sync from Syncro now" — kicks off the same backfill code path as
  // the nightly cron, then reloads the banner so any freshly-accrued
  // month appears as ``NOT YET DRAFTED`` immediately. Lets ops fix
  // "why isn't August showing?" without leaving the page.
  async function syncFromSyncro() {
    setSyncing(true);
    const t = toast.loading("Pulling paid invoices from Syncro…");
    try {
      const { data: res } = await api.post("/commissions/syncro-backfill/run-now");
      toast.success(
        res?.events_created
          ? `Backfill done — ${res.events_created} new commission event(s) accrued`
          : "Backfill done — no new events (all invoices already captured)",
        { id: t },
      );
      await reload();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Backfill failed — check Cron Health panel",
        { id: t },
      );
    } finally {
      setSyncing(false);
    }
  }

  if (!data) return null;
  const items = data.payouts || [];
  const timeline = data.timeline || [];
  // Show the banner if there's anything unpaid OR any prior month
  // with no accruals (so ops can see the gap and click Sync).
  const hasUnpaid = items.length > 0;
  const hasGap = timeline.some(t => t.status === "no_accruals");
  if (!hasUnpaid && !hasGap) return null;

  const total = items.reduce((a, p) => a + (p.amount_ex_gst || 0), 0);

  // Render one row per unpaid item — i.e. per (partner × period) pair
  // — so ops sees every individual commission that still needs paying
  // as its own line. Multiple partners in the same month get their
  // own rows so nothing is hidden inside a group. Sort key: period
  // oldest-first, then partner name so a two-month arrears reads as
  // June/Alice, June/Bob, July/Alice, July/Bob top-to-bottom.
  const sortedItems = [...items].sort((a, b) => {
    const p = (a.period || "").localeCompare(b.period || "");
    if (p !== 0) return p;
    return (a.partner_name || a.partner_code || "").localeCompare(
      b.partner_name || b.partner_code || ""
    );
  });
  const distinctPeriods = new Set(sortedItems.map((p) => p.period));
  const oldestPeriod = sortedItems[0]?.period;
  const deepHref = oldestPeriod ? `/admin/ledger?period=${oldestPeriod}` : "/admin/ledger";

  if (variant === "minimal") {
    return (
      <Link
        to={deepHref}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 text-amber-200 text-xs font-medium transition-colors w-fit"
      >
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
        </span>
        <span data-testid={`${testid}-summary`}>
          {sortedItems.length} unpaid commission{sortedItems.length === 1 ? "" : "s"}
          {distinctPeriods.size > 1 && ` across ${distinctPeriods.size} months`} ·{" "}
          <span className="text-amber-50 font-semibold">{money(total)}</span> ex-GST
        </span>
        <ChevronRight className="w-3.5 h-3.5 opacity-70" />
      </Link>
    );
  }

  // ---- variant === "compact" (Overview) ----
  // Header strip + a stacked list of per-month rows so each unpaid
  // month is visible on its own line with amount + partner count.
  return (
    <section
      data-testid={testid}
      className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] overflow-hidden"
    >
      <div className="px-4 py-3 flex items-center gap-3 border-b border-amber-500/20">
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
        </span>
        <AlertCircle className="w-4 h-4 text-amber-300 shrink-0" />
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-amber-100 font-semibold">
            {sortedItems.length} unpaid commission{sortedItems.length === 1 ? "" : "s"}
            {distinctPeriods.size > 1 && ` across ${distinctPeriods.size} months`}
          </span>
          <span className="text-[11px] text-amber-300/80 font-mono">
            Total <span data-testid={`${testid}-total`} className="text-amber-50 font-semibold">{money(total)}</span> ex-GST
          </span>
        </div>
        {/* Force-sync — pulls any missed Syncro paid invoices into
            ``commission_events`` immediately so a month that didn't
            accrue via the webhook still shows up here without waiting
            for the nightly backfill cron. */}
        <button
          type="button"
          onClick={syncFromSyncro}
          disabled={syncing}
          data-testid={`${testid}-sync`}
          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-amber-500/40 hover:border-amber-300 hover:bg-amber-500/10 px-3 py-1 text-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Fetch any missing paid invoices from Syncro and accrue them now"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span>{syncing ? "Syncing…" : "Sync from Syncro"}</span>
        </button>
      </div>

      {/* Last-6-months timeline strip — one chip per prior month
          showing its actual status (PAID / UNPAID / NOT DRAFTED /
          NO ACCRUALS). Guarantees every month is visible even if
          zero events landed, so ops sees "August · NO ACCRUALS"
          explicitly instead of a silent absence. */}
      {timeline.length > 0 && (
        <div
          data-testid={`${testid}-timeline`}
          className="px-4 py-2.5 border-b border-amber-500/15 bg-amber-500/[0.02] flex items-center gap-2 flex-wrap"
        >
          <span className="text-[10px] uppercase tracking-widest text-amber-300/70 font-semibold mr-1">
            Last 6 months
          </span>
          {timeline.map((t) => {
            const chip =
              t.status === "unpaid" ? { c: "bg-amber-500/25 text-amber-100 border-amber-500/60", l: "UNPAID" } :
              t.status === "not_drafted" ? { c: "bg-red-500/25 text-red-200 border-red-500/60", l: "NOT DRAFTED" } :
              t.status === "partly_drafted" ? { c: "bg-orange-500/25 text-orange-200 border-orange-500/60", l: "PARTLY DRAFTED" } :
              t.status === "paid" ? { c: "bg-emerald-500/20 text-emerald-200 border-emerald-500/50", l: "PAID" } :
              { c: "bg-slate-700/30 text-slate-400 border-slate-600/50", l: "NO ACCRUALS" };
            const amt = t.unpaid_ex_gst + t.undrafted_ex_gst || t.paid_ex_gst;
            return (
              <Link
                key={t.period}
                to={`/admin/ledger?period=${t.period}`}
                data-testid={`${testid}-timeline-${t.period}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-semibold hover:opacity-80 transition-opacity ${chip.c}`}
              >
                <span className="uppercase tracking-widest opacity-90">{periodLabel(t.period)}</span>
                <span className="opacity-70">·</span>
                <span>{chip.l}</span>
                {amt > 0 && (
                  <span className="opacity-80 font-mono">{money(amt)}</span>
                )}
              </Link>
            );
          })}
        </div>
      )}
      {/* One row per unpaid commission item — partner × period.
          Multiple partners in the same month get their own rows so
          nothing is hidden inside a group. */}
      <ul className="divide-y divide-amber-500/15" data-testid={`${testid}-list`}>
        {sortedItems.map((p) => {
          const isUndrafted = p.kind === "undrafted";
          const statusLabel = isUndrafted ? "Not yet drafted" : "Unpaid";
          const statusColor = isUndrafted
            ? "bg-red-500/20 text-red-200 border-red-500/40"
            : "bg-amber-500/20 text-amber-100 border-amber-500/40";
          const partnerLabel = p.partner_name || p.partner_code || "Partner";
          return (
            <li key={`${p.period}-${p.partner_code}-${p.id || "u"}`}>
              <Link
                to={`/admin/ledger?period=${p.period}`}
                data-testid={`${testid}-row-${p.period}-${p.partner_code}`}
                className="group flex items-center gap-3 px-4 py-2.5 hover:bg-amber-500/[0.08] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-amber-50 font-medium">
                      {partnerLabel}
                    </span>
                    <span className="text-[11px] text-amber-300/80 font-mono">
                      · {periodLabel(p.period)}
                    </span>
                    <span
                      data-testid={`${testid}-row-${p.period}-${p.partner_code}-status`}
                      className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border ${statusColor}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div className="text-[11px] text-amber-300/80 mt-0.5">
                    {p.event_count} accrued event{p.event_count === 1 ? "" : "s"}
                    {isUndrafted && " · click to draft payout"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-amber-50 font-semibold">
                    {money(p.amount_ex_gst)}
                  </div>
                  <div className="text-[10px] text-amber-300/70 uppercase tracking-widest">
                    ex-GST
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-300/70 group-hover:text-amber-100 group-hover:translate-x-0.5 transition-transform shrink-0" />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
